/**
 * Case Studies — real-life-inspired housing stability journeys.
 * Each story is a "What Would YOU Do?" scenario followed by a reveal
 * and discussion questions partner orgs can use in workshops.
 */

export const caseStudies = [
  {
    slug: "tanya",
    title: "Tanya's First Apartment",
    subtitle: "The Pre-Move Decision",
    accent: "primary", // sage green
    module: "Module 1 · Ready to Rent",
    tag: "Before You Sign",
    intro: {
      name: "Tanya",
      age: 28,
      situation: [
        "Works full-time as a home health aide",
        "Single mom, one child (age 5)",
        "Take-home pay: about $2,650/month",
        "Just found an apartment she loves — $1,300/month rent, near her child's school",
        "Has never made a budget",
      ],
      hook: "The landlord is pushing her to sign today because 'someone else is interested.' What should Tanya do?",
    },
    question: "What should Tanya do BEFORE signing the lease?",
    choices: [
      {
        label: "Sign it today so she doesn't lose the apartment",
        isBest: false,
        feedback:
          "Pressure to sign 'today or lose it' is one of the oldest tactics in the book. A landlord who is unwilling to give a family 24 hours to think may not be someone you want to rent from.",
      },
      {
        label: "Ask for 24 hours and build a quick budget first",
        isBest: true,
        feedback:
          "This is the smart move. 24 hours is a reasonable ask. A budget will show her whether she can actually afford the rent + utilities + food + child care.",
      },
      {
        label: "Sign now — she'll figure out the money later",
        isBest: false,
        feedback:
          "This is exactly how families end up behind on rent by month 3. 'Figuring it out' isn't a plan — it's a wish.",
      },
      {
        label: "Ask the landlord what percentage of tenants renew each year",
        isBest: true,
        feedback:
          "A great question. High renewal rates = a landlord tenants are happy with. Low renewal = red flag.",
      },
    ],
    reveal: {
      whatHappened:
        "Tanya asked for 24 hours. She used our budget worksheet and discovered that at $1,300 rent, she'd be at 49% of her take-home pay — well over the 30% rule. After utilities and child care, she'd have only $200/month left for food, transportation, and everything else.",
      outcome:
        "She negotiated a smaller unit in the same building at $1,050. Twelve months later, she had built her first-ever emergency fund of $600 and never missed a rent payment.",
      keyLessons: [
        "Never sign under pressure — 24 hours is a reasonable ask",
        "The 30% rule (rent ≤ 30% of take-home) keeps you housed long-term",
        "Small units in good buildings beat big units you can't afford",
        "A budget is proof — not just a plan",
      ],
    },
    discussion: [
      "Have you ever been pressured to make a housing decision quickly? What did you do?",
      "What's the difference between what an apartment costs and what you can afford?",
      "What do you say to a landlord who insists you sign the same day?",
    ],
  },

  {
    slug: "marcus",
    title: "Marcus Missed Rent",
    subtitle: "When Things Go Wrong",
    accent: "terracotta",
    module: "Module 3 · Housing Success",
    tag: "Recovering From a Setback",
    intro: {
      name: "Marcus",
      age: 34,
      situation: [
        "Warehouse worker — steady base pay + weekly overtime",
        "Lost his overtime hours last month due to cutbacks",
        "Rent is $1,150/month, always paid on time until now",
        "This month, he's $600 short on rent",
        "He hasn't answered his landlord's calls in 5 days",
      ],
      hook: "Marcus is embarrassed and scared. He's avoiding the calls. What mistakes is he making?",
    },
    question: "What should Marcus do RIGHT NOW to protect his housing?",
    choices: [
      {
        label: "Keep avoiding the landlord until he can pay the full amount",
        isBest: false,
        feedback:
          "This is the most common mistake — and the most damaging. Silence signals trouble. Every day of avoidance makes the landlord more likely to file for eviction.",
      },
      {
        label: "Call the landlord today, explain the situation, and propose a partial payment plan",
        isBest: true,
        feedback:
          "This is the right move. Landlords vastly prefer partial payment + communication over silence and $0. Most will work with a tenant who's honest and proactive.",
      },
      {
        label: "Put it on a credit card and figure out the rest later",
        isBest: false,
        feedback:
          "A credit card at 22% APR is one of the worst ways to pay rent. If Marcus doesn't fix the underlying income gap, he'll owe rent + credit card interest next month.",
      },
      {
        label: "Look into rental assistance programs and PA emergency funds today",
        isBest: true,
        feedback:
          "Yes. Programs like PA's Emergency Rental Assistance and local nonprofits exist for exactly this moment. Faster action = more options.",
      },
    ],
    reveal: {
      whatHappened:
        "Marcus finally called on day 6. His landlord — who had actually been calling to remind him about a scheduled inspection, not about rent — agreed to a partial payment on the 15th plus the rest by month-end. Marcus applied for emergency rental assistance and got approved in 3 weeks.",
      outcome:
        "The rest of his rent was covered by the assistance program. He kept his apartment. He also picked up two weekend delivery shifts to rebuild his cushion. Nine months later, he had three months of rent saved.",
      keyLessons: [
        "Silence is worse than bad news — landlords fear the unknown more than the truth",
        "Ask for a payment plan in writing (text or email) — always create a paper trail",
        "Rental assistance exists — use it early, not as a last resort",
        "One tough month doesn't have to become an eviction",
      ],
    },
    discussion: [
      "Why do people avoid difficult conversations with their landlord?",
      "What would you say if you had to call your landlord about missing rent?",
      "What are the local rental assistance resources in your community?",
    ],
  },

  {
    slug: "maria",
    title: "Maria's New Beginning",
    subtitle: "What Success Looks Like",
    accent: "gold",
    module: "Module 5 · Life Skills",
    tag: "Building Stability",
    intro: {
      name: "Maria",
      age: 41,
      situation: [
        "Just left an 8-month stay in transitional housing",
        "Signed her lease on a 1-bedroom apartment with her teenage daughter",
        "Received: apartment keys, household essentials, budget worksheet, maintenance guide",
        "Works part-time and receives a small support payment",
        "This is her first apartment on her own in over a decade",
      ],
      hook: "It's month 6. Maria's apartment is clean, rent is paid, savings are growing, and her daughter is thriving in school. What did she do right?",
    },
    question: "Which habits helped Maria succeed most?",
    choices: [
      {
        label: "She budgeted every paycheck the same day it hit",
        isBest: true,
        feedback:
          "Paying herself first — before spending — is the #1 habit that separates people who build savings from people who never do.",
      },
      {
        label: "She built a relationship with her landlord early",
        isBest: true,
        feedback:
          "Yes. She sent a friendly text after week 1 with her rent payment. When a minor issue came up in month 3, the landlord fixed it the same day.",
      },
      {
        label: "She kept everything to herself so no one would judge her",
        isBest: false,
        feedback:
          "This actually would have hurt her. Isolation is a warning sign in recovery from housing instability. Maria did the opposite — she stayed connected.",
      },
      {
        label: "She asked for help early when questions came up",
        isBest: true,
        feedback:
          "Absolutely. Asking for help is a skill — and Maria used it. She called her housing counselor twice in the first month with lease questions.",
      },
    ],
    reveal: {
      whatHappened:
        "Maria did all three of the right things — she budgeted every payday, built a warm and professional relationship with her landlord, and reached out for guidance when she had questions. She also kept a simple wall calendar in the kitchen with rent, utilities, and her daughter's school dates.",
      outcome:
        "By month 12, Maria had saved a full month of rent as an emergency fund. Her daughter's school reported dramatic improvements in attendance. Maria signed a second lease and began meeting with our Homeownership Readiness cohort.",
      keyLessons: [
        "Pay yourself first — automate savings before you can spend it",
        "Warm relationships with landlords prevent 90% of problems",
        "Asking for help is a strength, not a weakness",
        "Small daily systems (a wall calendar!) beat willpower",
      ],
    },
    discussion: [
      "What is one habit from Maria's story you could start this week?",
      "Who could be your 'housing counselor' — someone you could turn to with questions?",
      "What does success look like for you at the 6-month mark in your next apartment?",
    ],
  },
];
